import React, { KeyboardEventHandler, MouseEventHandler, KeyboardEvent } from 'react';
import { QueryResult, QueryConfig } from 'react-query';
import classNames from 'classnames';

export type AuthFileLinkProps<T> = {
  'aria-label'?: string;
  linkText: string;
  query: (arg: T, queryOptions?: QueryConfig<Blob>) => QueryResult<Blob>;
  queryArg: T;
  className?: string;
};

const AuthFileLink = <T extends unknown>({
  linkText,
  'aria-label': ariaLabel,
  query: useBlobQuery,
  queryArg,
  className,
}: AuthFileLinkProps<T>): JSX.Element => {
  const { refetch } = useBlobQuery(queryArg, { enabled: false });

  const handleBlobData = async () => {
    const blobData = await refetch();
    if (blobData && 'msSaveOrOpenBlob' in navigator && navigator.appVersion.toString().indexOf('.NET') > 0) {
      navigator.msSaveOrOpenBlob(blobData, 'MouSignedDocument.pdf');
    } else if (blobData) {
      const url = URL.createObjectURL(blobData);
      // if it's a PDF, or a common image, preview it in a new tab
      if (['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(blobData.type)) {
        window.open(url, '_blank');

        // otherwise, prompt user to download it
      } else {
        const fileName = 'CombinedMOUandTOS.zip';
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        URL.revokeObjectURL(url);
      }
    }
  };

  const handleClick: MouseEventHandler = async (event) => {
    event.preventDefault();
    await handleBlobData();
  };

  const handleKeyDown: KeyboardEventHandler = async (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await handleBlobData();
    }
  };

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className={classNames('cursor-pointer', className)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="link"
        aria-label={ariaLabel}
      >
        {linkText}
      </a>
    </>
  );
};

export default AuthFileLink;
